import api from './api';

/**
 * Service for handling multi-cloud operations
 */
const multiCloudService = {
  /**
   * Get available cloud providers
   * @returns {Promise<object>} List of available cloud providers
   */
  getProviders: async () => {
    try {
      const response = await api.get('/cloud/providers');
      return response.data.providers;
    } catch (error) {
      // Return empty array if endpoint unavailable to prevent UI errors
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  /**
   * Get user's cloud connections
   * @returns {Promise<object>} List of user's cloud connections
   */
  getConnections: async () => {
    try {
      const response = await api.get('/cloud/connections');
      return response.data.connections;
    } catch (error) {
      // Return empty array if endpoint unavailable to prevent UI errors
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },
  
  /**
   * Get authentication URL for a provider
   * @param {string} providerId - ID of the cloud provider
   * @returns {Promise<object>} Authentication URL and state
   */
  getAuthUrl: async (providerId) => {
    try {
      const response = await api.get(`/cloud/auth/${providerId}`);
      return response.data;
    } catch (error) {
      // Return fallback data if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return { authUrl: '#', state: 'unavailable' };
      }
      throw error;
    }
  },
  
  /**
   * Connect to a cloud provider
   * @param {string} providerId - ID of the cloud provider
   * @param {object} authData - Authentication data from OAuth callback
   * @returns {Promise<object>} New connection details
   */
  connectProvider: async (providerId, authData) => {
    try {
      const response = await api.post(`/cloud/connect/${providerId}`, authData);
      return response.data;
    } catch (error) {
      // Return fallback data if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return { success: false, message: 'Cloud integration not available' };
      }
      throw error;
    }
  },
  
  /**
   * Disconnect a cloud connection
   * @param {string} connectionId - ID of the connection to disconnect
   * @returns {Promise<object>} Result of disconnection
   */
  disconnectConnection: async (connectionId) => {
    try {
      const response = await api.delete(`/cloud/connections/${connectionId}`);
      return response.data;
    } catch (error) {
      // Return fallback data if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return { success: true };
      }
      throw error;
    }
  },
  
  /**
   * List files in a cloud provider
   * @param {string} connectionId - ID of the cloud connection
   * @param {string} path - Path to list (default: root)
   * @returns {Promise<object>} List of files and folders
   */
  listFiles: async (connectionId, path = 'root') => {
    try {
      const response = await api.get(`/cloud/${connectionId}/files`, {
        params: { path }
      });
      return response.data;
    } catch (error) {
      // Return empty data if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return { 
          files: [], 
          folders: [], 
          path: path,
          parentPath: path === 'root' ? null : 'root' 
        };
      }
      throw error;
    }
  },
  
  /**
   * Download a file from cloud
   * @param {string} connectionId - ID of the cloud connection
   * @param {string} fileId - ID of the file in the cloud
   * @returns {Promise<Blob>} File blob for download
   */
  downloadFile: async (connectionId, fileId) => {
    try {
      const response = await api.get(`/cloud/${connectionId}/files/${fileId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      // Can't provide fallback for binary data, must throw
      throw error;
    }
  },
  
  /**
   * Upload a file to cloud
   * @param {string} connectionId - ID of the cloud connection
   * @param {File} file - File to upload
   * @param {string} path - Path to upload to (default: root)
   * @param {function} onProgress - Progress callback
   * @returns {Promise<object>} Uploaded file details
   */
  uploadFile: async (connectionId, file, path = 'root', onProgress) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', path);
      
      const response = await api.post(`/cloud/${connectionId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        }
      });
      
      return response.data;
    } catch (error) {
      // Notify progress callback of failure
      if (onProgress) {
        onProgress(-1); // Use negative value to indicate error
      }
      
      // Return fallback data if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return { 
          success: false, 
          message: 'Cloud upload not available',
          file: {
            id: 'local-' + Date.now(),
            name: file.name,
            size: file.size,
            mimeType: file.type,
            path: path
          }
        };
      }
      throw error;
    }
  },
  
  /**
   * Create a sync job between local and cloud storage
   * @param {string} connectionId - ID of the cloud connection
   * @returns {Promise<object>} Sync job details
   */
  createSyncJob: async (connectionId) => {
    try {
      // Send expected JSON object with destination; source, schedule, filters can be omitted or null
      const payload = { destination: connectionId };
      const response = await api.post('/cloud/sync', payload);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { success: false, message: 'Cloud sync not available' };
      }
      throw error;
    }
  },
  
  /**
   * Get status of sync jobs
   * @returns {Promise<object>} List of sync jobs with status
   */
  getSyncJobs: async () => {
    try {
      const response = await api.get('/cloud/sync');
      return response.data.syncJobs;
    } catch (error) {
      // Return empty array if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },
  
  /**
   * Cancel a sync job
   * @param {string} jobId - ID of the sync job
   * @returns {Promise<object>} Result of cancellation
   */
  cancelSyncJob: async (jobId) => {
    try {
      const response = await api.post(`/cloud/sync/${jobId}/cancel`);
      return response.data;
    } catch (error) {
      // Return fallback data if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return { success: true };
      }
      throw error;
    }
  },
  
  /**
   * Configure auto-sync settings
   * @param {string} connectionId - ID of the cloud connection
   * @param {object} config - Auto-sync configuration
   * @returns {Promise<object>} Updated auto-sync settings
   */
  configureAutoSync: async (connectionId, config) => {
    try {
      const response = await api.put(`/cloud/${connectionId}/auto-sync`, config);
      return response.data;
    } catch (error) {
      // Return fallback data if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return { 
          success: false, 
          message: 'Auto-sync configuration not available',
          settings: config
        };
      }
      throw error;
    }
  },
  
  /**
   * Get multi-cloud dashboard data
   * @returns {Promise<object>} Cloud usage statistics and information
   */
  getDashboard: async () => {
    try {
      const response = await api.get('/cloud/dashboard');
      return response.data;
    } catch (error) {
      // Return fallback data if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return { 
          connections: [],
          totalSize: 0,
          totalFiles: 0,
          recentActivity: []
        };
      }
      throw error;
    }
  },
  
  /**
   * Import files from cloud provider
   * @param {string} connectionId - ID of the cloud connection
   * @param {string[]} fileIds - IDs of files to import
   * @param {string} targetFolderId - Target folder ID to import files to
   * @returns {Promise<object>} Import job details
   */
  importFiles: async (connectionId, fileIds, targetFolderId) => {
    try {
      const response = await api.post(`/cloud/${connectionId}/import`, {
        fileIds,
        targetFolderId
      });
      return response.data;
    } catch (error) {
      // Return fallback data if endpoint doesn't exist
      if (error?.response?.status === 404) {
        return { 
          success: false, 
          message: 'Cloud import not available',
          jobId: 'unavailable'
        };
      }
      throw error;
    }
  }
};

export default multiCloudService; 